import React, { useContext } from 'react'
import styled from 'styled-components'
import FormatCurrency from 'components/Common/FormatCurrency'
import LinePlaneFlyIcon from 'components/Luxkit/Icons/line/LinePlaneFlyIcon'
import useDefaultAirport from 'hooks/useDefaultAirport'
import Caption from 'components/Luxkit/Typography/Caption'
import Heading from 'components/Luxkit/Typography/Heading'
import { getDepositAmountPercentage, isDepositAvailableOnOffer, isDepositEnabledForOfferType } from 'lib/depositsUtils'
import CurrencyContext from 'contexts/currencyContext'
import SecureWithDepositTagAndTooltip from 'components/Common/SecureWithDeposit/SecureWithDepositTagAndTooltip'
import getValueOffPercent from 'lib/getValueOffPercent'
import Pill from 'components/Luxkit/Pill'
import TextButton from 'components/Luxkit/Button/TextButton'
import Group from 'components/Luxkit/Group'
import PaletteProvider from 'contexts/PaletteProvider'
import CSSBreakpoint from 'components/Common/CSSBreakpoint'
import TaxesAndFees from 'components/OfferPage/PackageOption/Pricing/TaxesAndFees'
import CaptionTextBlock from 'components/Luxkit/TextBlocks/CaptionTextBlock'
import { getDefaultDepositAmountPercentage } from 'checkout/selectors/featureConfig/deposit'
import { connect } from 'react-redux'

const Pricing = styled.div`
  display: flex;
  flex-direction: column;
`

interface MappedProps {
  defaultDepositAmountPercentage: number;
}

interface Props extends MappedProps {
  offer: App.Offer | App.OfferSummary;
}

function HeroOfferTileBundlePricing(props: Props) {
  const { offer, defaultDepositAmountPercentage } = props
  const defaultAirport = useDefaultAirport()
  const currentCurrency = useContext(CurrencyContext)

  const flightPrice = offer.flightPrices[defaultAirport?.code]

  if (!flightPrice) {
    return (
      <Pricing>
        <CaptionTextBlock variant="medium" weight="bold" startIcon={<LinePlaneFlyIcon />}>
          Hotel + Flights packages
        </CaptionTextBlock>
      </Pricing>
    )
  }

  const totalPrice = offer.lowestPricePackage.price + flightPrice
  const totalValue = offer.lowestPricePackage.value + flightPrice
  const showValue = offer.lowestPricePackage.shouldDisplayValue && totalValue > totalPrice

  const showSecureWithDepositTag = isDepositEnabledForOfferType(offer.type) &&
    isDepositAvailableOnOffer(offer, currentCurrency, null, offer.lowestPricePackage.price)
  const depositAmountPercentage = getDepositAmountPercentage(offer, defaultDepositAmountPercentage)

  return (
    <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="end">
      <Group direction="vertical">
        {showSecureWithDepositTag && (
          <PaletteProvider type="default">
            <SecureWithDepositTagAndTooltip
              depositAmountPercentage={depositAmountPercentage}
              offerType={offer.type}
              depositThresholds={offer.depositThresholds}
              depositAmount={offer.depositAmount}
            />
          </PaletteProvider>
        )}

        {showValue && (
          <div>
            <PaletteProvider type="default">
              <Pill kind="primary" variant="success">
                Save up to {getValueOffPercent(totalValue, totalPrice)}%
              </Pill>
            </PaletteProvider>
          </div>
        )}
        <CaptionTextBlock variant="medium" weight="bold" startIcon={<LinePlaneFlyIcon />}>
          Hotel + Flights
        </CaptionTextBlock>
        <Caption variant="medium" colour="neutral-three">{offer.tileDurationLabel} from</Caption>
        <Heading variant="heading3">
          <FormatCurrency value={Math.ceil(totalPrice)} />
          <Caption variant="medium" as="span" colour="neutral-two">/package</Caption>
        </Heading>
        <Caption variant="medium" colour="neutral-three">
          2 adults
          <TaxesAndFees
            property={offer.property}
            taxAmount={offer.lowestPricePackage?.taxesAndFees}
          />
        </Caption>
      </Group>
      <CSSBreakpoint min="md">
        <TextButton kind="primary">
          View offer
        </TextButton>
      </CSSBreakpoint>
    </Group>
  )
}

const mapStateToProps = (state: App.State): MappedProps => ({
  defaultDepositAmountPercentage: getDefaultDepositAmountPercentage(state),
})

export default connect(mapStateToProps)(HeroOfferTileBundlePricing)
